import { computed, ref, shallowRef } from "vue"
import { defineStore } from "pinia"

import { WARNING_THRESHOLD } from "@/features/screening/writing/constants"
import type { ChatMessage, WelcomeRunResult } from "@/types/screening-writing"

const STORAGE_KEY = "screening-writing-thread"

export const useScreeningWritingMessagesStore = defineStore(
  "screeningWritingMessages",
  () => {
    const messages = ref<ChatMessage[]>([])
    const suggestions = ref<string[]>([])
    const threadId = ref<string>(localStorage.getItem(STORAGE_KEY) ?? "")
    const isAssistantThinking = ref(false)
    const consecutiveErrors = ref(0)
    const cameFromWelcomePage = ref(false)

    // Pending chat has been init in welcome page but we redirect to chat page before ending the run
    // We keep this promise to continue the same streaming in the chat page
    const pendingWelcomePageResponse =
      shallowRef<Promise<WelcomeRunResult> | null>(null)

    const userMessagesCount = computed(
      () => messages.value.filter((m) => m.role === "user").length
    )

    const showWarningLimit = computed(
      () => userMessagesCount.value >= WARNING_THRESHOLD
    )

    const setThreadId = (id: string) => {
      threadId.value = id
      if (id) {
        localStorage.setItem(STORAGE_KEY, id)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    const upsertMessage = (msg: ChatMessage) => {
      const idx = messages.value.findIndex((m) => m.id === msg.id)
      if (idx >= 0) {
        messages.value[idx] = { ...messages.value[idx], ...msg }
      } else {
        messages.value.push(msg)
      }
    }

    const appendToMessage = (id: string, chunk: string) => {
      const msg = messages.value.find((m) => m.id === id)
      if (msg) {
        msg.content += chunk
      } else {
        messages.value.push({ id, role: "assistant", content: chunk })
        isAssistantThinking.value = false
      }
    }

    const reset = () => {
      setThreadId("")
      messages.value = []
      suggestions.value = []
      consecutiveErrors.value = 0
      isAssistantThinking.value = false
      cameFromWelcomePage.value = false
      pendingWelcomePageResponse.value = null
    }

    return {
      messages,
      suggestions,
      threadId,
      isAssistantThinking,
      consecutiveErrors,
      cameFromWelcomePage,
      pendingWelcomePageResponse,
      userMessagesCount,
      showWarningLimit,
      setThreadId,
      upsertMessage,
      appendToMessage,
      reset,
    }
  }
)
